// src/pages/Checkout.js
// HomeIQa — Checkout with PayPal integration
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { PayPalButtons } from '@paypal/react-paypal-js';
import { createPayPalOrder, capturePayPalOrder } from '../firebase';
import Logo from '../components/Logo';

export default function Checkout() {
  const navigate = useNavigate();
  const [intake, setIntake] = useState(null);
  const [agreed, setAgreed] = useState(false);
  const [error, setError] = useState('');
  const [processing, setProcessing] = useState(false);
  const [promoCode, setPromoCode] = useState('');
  const [promoApplied, setPromoApplied] = useState(null); // { discount: 100, label: "FREE REPORT" } or null
  const [promoError, setPromoError] = useState('');
  const [promoChecking, setPromoChecking] = useState(false);

  useEffect(() => {
    const saved = sessionStorage.getItem('homeIQa_intake');
    if (!saved) { navigate('/start'); return; }
    setIntake(JSON.parse(saved));
  }, [navigate]);

  if (!intake) return null;

  const ADDON_PRICES = { 'Crime, schools & livability': 9, 'Flood & climate risk': 12, 'Permit & lien history': 14 };
  const addonTotal = (intake.addons || []).reduce((sum, a) => sum + (ADDON_PRICES[a] || 0), 0);
  const subtotal = 29 + addonTotal;
  const discount = promoApplied ? (promoApplied.discount === 100 ? subtotal : Math.min(promoApplied.discount, subtotal)) : 0;
  const total = Math.max(0, subtotal - discount);

  // Validate promo code via Cloud Function
  async function handleApplyPromo() {
    if (!promoCode.trim()) return;
    setPromoError('');
    setPromoChecking(true);
    try {
      const { httpsCallable } = await import('firebase/functions');
      const { functions } = await import('../firebase');
      const validatePromo = httpsCallable(functions, 'validatePromoCode');
      const result = await validatePromo({ code: promoCode.trim().toUpperCase() });
      if (result.data.valid) {
        setPromoApplied({ discount: result.data.discount, label: result.data.label });
        setPromoError('');
      } else {
        setPromoError(result.data.message || 'Invalid promo code.');
        setPromoApplied(null);
      }
    } catch (e) {
      setPromoError('Could not verify promo code. Please try again.');
      setPromoApplied(null);
    }
    setPromoChecking(false);
  }

  function handleRemovePromo() {
    setPromoApplied(null);
    setPromoCode('');
    setPromoError('');
  }

  // Create PayPal order — calls our Firebase Cloud Function
  async function handleCreateOrder() {
    try {
      const result = await createPayPalOrder({ amount: total, intake });
      return result.data.orderID;
    } catch (e) {
      setError('Could not create order. Please try again.');
      throw e;
    }
  }

  // Capture PayPal payment — triggers report generation
  async function handleApprove(data) {
    setProcessing(true);
    setError('');
    try {
      const result = await capturePayPalOrder({
        orderID: data.orderID,
        intake,
        addons: intake.addons || []
      });
      if (result.data.success) {
        sessionStorage.removeItem('homeIQa_intake');
        navigate(`/generating/${result.data.orderId}`);
      } else {
        setError('Payment captured but report generation failed. We will email you shortly.');
      }
    } catch (e) {
      setError('Payment processing error. Please contact support@homeIQa.com');
      setProcessing(false);
    }
  }

  return (
    <div style={styles.page}>
      <div style={styles.header}><Logo /></div>

      <div style={styles.layout}>
        {/* ORDER SUMMARY */}
        <div style={styles.summary}>
          <div style={styles.summaryTitle}>Order summary</div>
          <div style={styles.summaryAddress}>{intake.address}</div>

          <div style={styles.lineItem}>
            <span>HomeIQa valuation report</span>
            <span>$29.00</span>
          </div>
          {(intake.addons || []).map(addon => (
            <div key={addon} style={styles.lineItem}>
              <span style={{ fontSize: 13 }}>{addon}</span>
              <span style={{ fontSize: 13 }}>+${ADDON_PRICES[addon]}</span>
            </div>
          ))}
          {/* PROMO CODE */}
          <div style={{ padding: '12px 0', borderBottom: '1px solid rgba(107,30,46,0.08)' }}>
            {!promoApplied ? (
              <div>
                <div style={{ display: 'flex', gap: 8 }}>
                  <input
                    type="text"
                    placeholder="Promo code"
                    value={promoCode}
                    onChange={e => { setPromoCode(e.target.value.toUpperCase()); setPromoError(''); }}
                    onKeyDown={e => e.key === 'Enter' && handleApplyPromo()}
                    style={{ flex: 1, padding: '8px 12px', borderRadius: 6, border: '1px solid rgba(107,30,46,0.15)', fontSize: 13, fontFamily: "'DM Sans', sans-serif", letterSpacing: '0.05em', textTransform: 'uppercase' }}
                  />
                  <button
                    onClick={handleApplyPromo}
                    disabled={promoChecking || !promoCode.trim()}
                    style={{ padding: '8px 16px', borderRadius: 6, border: 'none', background: '#4A1220', color: 'white', fontSize: 12, fontWeight: 600, cursor: 'pointer', opacity: promoChecking || !promoCode.trim() ? 0.5 : 1, fontFamily: "'DM Sans', sans-serif" }}
                  >
                    {promoChecking ? '...' : 'Apply'}
                  </button>
                </div>
                {promoError && <div style={{ fontSize: 11, color: '#8A1A1A', marginTop: 6 }}>{promoError}</div>}
              </div>
            ) : (
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: '#EAF7EE', padding: '8px 12px', borderRadius: 6 }}>
                <div>
                  <span style={{ fontSize: 12, fontWeight: 600, color: '#1A7042' }}>✓ {promoApplied.label}</span>
                  <span style={{ fontSize: 11, color: '#5A5A5A', marginLeft: 8 }}>({promoCode})</span>
                </div>
                <button onClick={handleRemovePromo} style={{ background: 'none', border: 'none', color: '#8A1A1A', fontSize: 11, cursor: 'pointer', textDecoration: 'underline' }}>Remove</button>
              </div>
            )}
          </div>

          {promoApplied && (
            <div style={{ ...styles.lineItem, color: '#1A7042', fontSize: 13 }}>
              <span>Discount ({promoApplied.label})</span>
              <span>-${discount.toFixed(2)}</span>
            </div>
          )}

          <div style={styles.divider} />
          <div style={{ ...styles.lineItem, fontWeight: 600, fontSize: 18 }}>
            <span>Total</span>
            <span style={{ color: total === 0 ? '#1A7042' : '#6B1E2E' }}>{total === 0 ? 'FREE' : `$${total.toFixed(2)}`}</span>
          </div>

          <div style={styles.includes}>
            <div style={styles.includesTitle}>What's included:</div>
            {['12-page personalized valuation report', 'Verified against county property records', 'Live comparable sales data', 'Equity & net proceeds breakdown', 'Optimal selling window analysis', 'Top 3 improvement ROI picks', 'Instant PDF download via email'].map(item => (
              <div key={item} style={styles.includeItem}>
                <span style={styles.checkmark}>✓</span> {item}
              </div>
            ))}
          </div>
        </div>

        {/* PAYMENT */}
        <div style={styles.paymentCard}>
          <div style={styles.paymentTitle}>Complete your purchase</div>
          <div style={styles.secureNote}>🔒 Secure payment via PayPal</div>

          <div style={styles.agreement}>
            <label style={styles.agreeLabel}>
              <input
                type="checkbox"
                checked={agreed}
                onChange={e => setAgreed(e.target.checked)}
                style={{ marginRight: 10, marginTop: 2, flexShrink: 0 }}
              />
              <span>
                I agree to the <a href="/terms" target="_blank" style={styles.link}>Terms & Conditions</a> and <a href="/refund-policy" target="_blank" style={styles.link}>Refund Policy</a>. I understand this is a digital product with no refund once delivered.
              </span>
            </label>
          </div>

          {agreed ? (
            total === 0 ? (
              <button
                onClick={async () => {
                  setProcessing(true);
                  setError('');
                  try {
                    const { httpsCallable } = await import('firebase/functions');
                    const { functions, db } = await import('../firebase');
                    const { collection, addDoc, serverTimestamp } = await import('firebase/firestore');
                    // Create order directly in Firestore (no PayPal needed)
                    const orderRef = await addDoc(collection(db, 'orders'), {
                      intake,
                      addons: intake.addons || [],
                      total: 0,
                      promoCode: promoCode,
                      promoDiscount: discount,
                      status: 'paid',
                      paymentMethod: 'promo_code',
                      createdAt: serverTimestamp(),
                    });
                    sessionStorage.removeItem('homeIQa_intake');
                    navigate(`/generating/${orderRef.id}`);
                  } catch (e) {
                    setError('Could not process your free report. Please try again.');
                    setProcessing(false);
                  }
                }}
                disabled={processing}
                style={{ width: '100%', padding: '14px 24px', borderRadius: 8, border: 'none', background: 'linear-gradient(135deg, #1A7042, #2D8F5A)', color: 'white', fontSize: 16, fontWeight: 700, cursor: 'pointer', fontFamily: "'DM Sans', sans-serif", opacity: processing ? 0.6 : 1, marginBottom: 16 }}
              >
                {processing ? 'Generating your report...' : '🎉 Get Your Free Report'}
              </button>
            ) : (
              <div style={styles.paypalWrap}>
                <PayPalButtons
                  style={{ layout: 'vertical', color: 'gold', shape: 'rect', label: 'pay' }}
                  disabled={processing}
                  createOrder={handleCreateOrder}
                  onApprove={handleApprove}
                  onError={(e) => setError('PayPal encountered an error. Please try again.')}
                  onCancel={() => setError('')}
                />
              </div>
            )
          ) : (
            <div style={styles.agreeBefore}>Please accept the terms above to proceed with payment.</div>
          )}

          {processing && (
            <div style={styles.processingMsg}>
              <div style={styles.spinner} />
              Processing your payment and generating your report...
            </div>
          )}

          {error && <div style={styles.errorBox}>{error}</div>}

          <div style={styles.disclaimer}>
            HomeIQa reports are for informational purposes only and do not constitute a licensed appraisal. Not for use in mortgage lending or legal proceedings.
          </div>
        </div>
      </div>
    </div>
  );
}

const styles = {
  page: { minHeight: '100vh', background: '#FAF7F2', fontFamily: "'DM Sans', sans-serif" },
  header: { background: '#4A1220', borderBottom: '4px solid #C9A84C', padding: '14px 32px' },
  layout: { maxWidth: 900, margin: '40px auto', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 28, padding: '0 24px' },
  summary: { background: 'white', borderRadius: 12, padding: '28px 32px', border: '1px solid rgba(107,30,46,0.1)', alignSelf: 'start' },
  summaryTitle: { fontFamily: "'Cormorant Garamond', serif", fontSize: 22, fontWeight: 500, color: '#4A1220', marginBottom: 6 },
  summaryAddress: { fontSize: 13, color: '#5A5A5A', marginBottom: 20, padding: '8px 12px', background: '#FAF7F2', borderRadius: 6, borderLeft: '3px solid #C9A84C' },
  lineItem: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 0', borderBottom: '1px solid rgba(107,30,46,0.08)', fontSize: 14, color: '#1C1C1C' },
  divider: { height: 0, margin: '4px 0' },
  includes: { marginTop: 20, padding: '16px', background: '#FAF7F2', borderRadius: 8 },
  includesTitle: { fontSize: 11, fontWeight: 500, letterSpacing: '0.08em', textTransform: 'uppercase', color: '#9A9A9A', marginBottom: 10 },
  includeItem: { fontSize: 12, color: '#5A5A5A', padding: '4px 0', display: 'flex', alignItems: 'flex-start', gap: 8 },
  checkmark: { color: '#1A7042', fontWeight: 700, flexShrink: 0 },
  paymentCard: { background: 'white', borderRadius: 12, padding: '28px 32px', border: '1px solid rgba(107,30,46,0.1)', alignSelf: 'start' },
  paymentTitle: { fontFamily: "'Cormorant Garamond', serif", fontSize: 22, fontWeight: 500, color: '#4A1220', marginBottom: 4 },
  secureNote: { fontSize: 12, color: '#5A5A5A', marginBottom: 20 },
  agreement: { marginBottom: 20, padding: '14px', background: '#FAF7F2', borderRadius: 8, border: '1px solid rgba(107,30,46,0.1)' },
  agreeLabel: { display: 'flex', alignItems: 'flex-start', fontSize: 13, color: '#5A5A5A', lineHeight: 1.5, cursor: 'pointer' },
  link: { color: '#6B1E2E', textDecoration: 'underline' },
  paypalWrap: { marginBottom: 16 },
  agreeBefore: { fontSize: 13, color: '#9A9A9A', padding: '12px', background: '#FAF7F2', borderRadius: 6, textAlign: 'center' },
  processingMsg: { display: 'flex', alignItems: 'center', gap: 10, fontSize: 13, color: '#5A5A5A', padding: '12px', background: '#EAF7EE', borderRadius: 6, marginBottom: 12 },
  spinner: { width: 16, height: 16, border: '2px solid #1A7042', borderTopColor: 'transparent', borderRadius: '50%', animation: 'spin 0.8s linear infinite', flexShrink: 0 },
  errorBox: { padding: '12px', background: '#FEF0F0', color: '#8A1A1A', fontSize: 13, borderRadius: 6, marginBottom: 12 },
  disclaimer: { fontSize: 11, color: '#9A9A9A', lineHeight: 1.6, marginTop: 16, paddingTop: 16, borderTop: '1px solid rgba(107,30,46,0.08)' }
};
